import type { PostLoginResponse } from '@repo/api/auth/authService';
import type { LoginSchema } from '@repo/schemas/auth/login';
import type { UseMutateAsyncFunction } from '@tanstack/react-query';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { ErrorMessageHandler } from '@repo/lib/errorMessageHandler';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { isParent } from '@repo/lib/user-lvl/isParent';
import { loginSchema } from '@repo/schemas/auth/login';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';

import { useSetCredentials } from '@/store/auth-store';
import { getAvatar } from '@/utils/getAvatar';

export function useLoginForm(mutateAsync: UseMutateAsyncFunction<PostLoginResponse, Error, LoginSchema, unknown>) {
  const navigate = useNavigate();
  const location = useLocation();

  const setCredentials = useSetCredentials();

  const form = useForm<LoginSchema>({
    resolver: standardSchemaResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = (data: LoginSchema) => {
    mutateAsync(data)
      .then(({ redirectURL, user }) => {
        const from: string = location.state?.from?.pathname || '/';
        const prevUserId: number | undefined = location.state?.userId || undefined;
        // A parent has no access to the route they were bounced from, so
        // restoring it would only land them on /notAllowed.
        const landingURL: string = isParent(user.accountType) ? '/parent' : prevUserId === user.id ? from : '/';
        const newRedirectURL: string = redirectURL === '/' ? landingURL : redirectURL;
        setCredentials({ ...user, avatarURL: getAvatar(user.avatarURL) ?? null });
        navigate(newRedirectURL, { replace: true });
      })
      .catch((error) => {
        const errorHandler = new ErrorMessageHandler(baseErrorMessages);
        const { detail } = errorHandler.processError(error);
        form.setError('password', { type: 'manual', message: detail });
        form.setError('email', { type: 'manual', message: detail });
      });
  };

  return {
    form: form,
    onSubmit: onSubmit,
  };
}
